import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import ExploreIcon from "../../Assets/services/Explore.svg";
import BankIcon from "../../Assets/services/Bank.svg";
import LawyerIcon from "../../Assets/services/Lawyer.svg";
import ParcelIcon from "../../Assets/services/Parcel.svg";
import TowIcon from "../../Assets/services/Tow.svg";
import { useTranslation } from "react-i18next";
import FloatingBackground from "./FloatingBackground";
import ServicePill from "./ServicePill";
import StatsCounter from "./StatsCounter";

const services = [
  {
    id: "explore",
    title: "Explore",
    description:
      "Discover trusted services and nearby providers wherever you are.",
    icon: ExploreIcon,
    position: { left: "20%", top: "75%", width: 234 },
    node: { x: 46, y: 86 },
    float: 6,
    delay: 0,
    duration: 6.4,
  },
  {
    id: "banking",
    title: "Banking",
    description:
      "Get practical support for accounts, payments, and financial paperwork.",
    icon: BankIcon,
    position: { left: "0%", top: "2%" },
    node: { x: 17, y: 12 },
    float: 7,
    delay: 0.4,
    duration: 7,
  },
  {
    id: "parcel",
    title: "Parcel Delivery",
    description:
      "Move parcels across the city with reliable local delivery partners.",
    icon: ParcelIcon,
    position: { right: "0%", top: "0%" },
    node: { x: 82, y: 9 },
    float: 5,
    delay: 0.2,
    duration: 7.4,
  },
  {
    id: "legal",
    title: "Legal Services",
    description:
      "Connect with qualified help for documents, guidance, and formal needs.",
    icon: LawyerIcon,
    position: { left: "0%", top: "46%" },
    node: { x: 15, y: 52 },
    float: 4,
    delay: 0.7,
    duration: 6.7,
  },
  {
    id: "towing",
    title: "Towing Assistance",
    description:
      "Request roadside recovery and towing support when plans change.",
    icon: TowIcon,
    position: { right: "2%", top: "40%" },
    node: { x: 83, y: 47 },
    float: 8,
    delay: 0.52,
    duration: 6.9,
  },
];

// Dashed connectors threading the pills into a loop (drawn in 0-100 space).
const connectorOrder = ["banking", "parcel", "towing", "explore", "legal"];

const stats = [
  { value: 5000, suffix: "+", label: "Service Providers" },
  { value: 25, suffix: "+", label: "Cities Covered" },
  // { value: 50000, suffix: "+", label: "Successful Requests" },
];

const ServicesSection = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const reducedMotion = useReducedMotion();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const mapY = useTransform(
    scrollYProgress,
    [0, 1],
    reducedMotion ? [0, 0] : [-24, 24],
  );
  const glowY = useTransform(
    scrollYProgress,
    [0, 1],
    reducedMotion ? [0, 0] : [18, -18],
  );

  useEffect(() => {
    if (!sectionRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );

    observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  // Default-highlight the primary "explore" pill until the user hovers another.
  const activeId = hoveredId || "explore";

  const localizedServices = useMemo(
    () =>
      services.map((service) => ({
        ...service,
        title: t(`svc_${service.id}_title`),
        description: t(`svc_${service.id}_desc`),
      })),
    [t],
  );

  const connectorSegments = useMemo(() => {
    const byId = Object.fromEntries(services.map((s) => [s.id, s.node]));
    return connectorOrder.map((id, index) => {
      const from = byId[id];
      const to = byId[connectorOrder[(index + 1) % connectorOrder.length]];
      return { id, from, to };
    });
  }, []);

  const headingVariants = useMemo(
    () => ({
      hidden: { opacity: 0, y: 18 },
      visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
      },
    }),
    [],
  );

  return (
    <Box
      ref={sectionRef}
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        py: { xs: 8, md: 11, lg: 13 },
        backgroundColor: "#0C5A4C",
        isolation: "isolate",
      }}
    >
      <FloatingBackground mapY={mapY} glowY={glowY} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: { xs: 6, md: 5, lg: 8 },
          }}
        >
          {/* Left: copy + stats */}
          <Stack
            component={motion.div}
            variants={headingVariants}
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
            spacing={2.4}
            sx={{
              flex: { md: "0 0 44%" },
              maxWidth: { xs: 620, md: "none" },
              textAlign: { xs: "center", md: "start" },
              alignItems: { xs: "center", md: "flex-start" },
            }}
          >
            <Typography
              sx={{
                color: "#00D5C8",
                fontSize: { xs: "0.74rem", md: "0.8rem" },
                fontWeight: 800,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
              }}
            >
              {t("services_eyebrow")}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: "#FFFFFF",
                fontSize: { xs: "2rem", sm: "2.75rem", md: "3.4rem" },
                fontWeight: 900,
                lineHeight: { xs: 1.05, md: 1.02 },
                letterSpacing: 0,
              }}
            >
              {t("services_title")}
            </Typography>
            <Typography
              component={motion.p}
              initial={{ opacity: 0, y: 18 }}
              animate={isVisible ? { opacity: 1, y: 0 } : undefined}
              transition={{
                duration: 0.72,
                delay: 0.15,
                ease: [0.16, 1, 0.3, 1],
              }}
              sx={{
                color: "rgba(224,244,242,0.72)",
                fontSize: { xs: "0.95rem", md: "1.08rem" },
                lineHeight: 1.7,
                maxWidth: 460,
                letterSpacing: 0,
              }}
            >
              {t("services_subtitle")}
            </Typography>

            <Stack
              component={motion.div}
              direction="row"
              flexWrap="wrap"
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : undefined}
              transition={{
                duration: 0.72,
                delay: 0.32,
                ease: [0.16, 1, 0.3, 1],
              }}
              sx={{
                gap: 1.5,
                pt: 1,
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              {stats.map((stat) => (
                <StatsCounter
                  key={stat.label}
                  {...stat}
                  isVisible={isVisible}
                />
              ))}
            </Stack>

            <Stack
              component={motion.div}
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : undefined}
              transition={{
                duration: 0.72,
                delay: 0.42,
                ease: [0.16, 1, 0.3, 1],
              }}
              sx={{
                pt: 1,
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "stretch", md: "flex-start" },
              }}
            >
              <Button
                component="a"
                href="https://click.aroundu.link/AV19/8w92jhhq"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  px: { xs: 2.5, md: 3 },
                  py: { xs: 1.05, md: 1.15 },
                  minHeight: 46,
                  borderRadius: 999,
                  backgroundColor: "#00D5C8",
                  color: "#06231F",
                  fontSize: { xs: "0.9rem", md: "0.95rem" },
                  fontWeight: 800,
                  letterSpacing: 0,
                  textTransform: "none",
                  boxShadow: "0 16px 36px rgba(0,213,200,0.28)",
                  "&:hover": {
                    backgroundColor: "#00c2b6",
                    boxShadow: "0 18px 40px rgba(0,213,200,0.36)",
                  },
                  "&:focus-visible": {
                    outline: "3px solid rgba(0,213,200,0.45)",
                    outlineOffset: "3px",
                  },
                }}
              >
                {t("get_app")}
              </Button>
              <Button
                component="a"
                href="/arounder-registration"
                sx={{
                  px: { xs: 2.5, md: 3 },
                  py: { xs: 1.05, md: 1.15 },
                  minHeight: 46,
                  borderRadius: 999,
                  backgroundColor: "transparent",
                  color: "#ffffff",
                  border: "1px solid rgba(255,255,255,0.4)",
                  fontSize: { xs: "0.9rem", md: "0.95rem" },
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderColor: "rgba(255,255,255,0.7)",
                  },
                }}
              >
                {t("arounder_registration")}
              </Button>
            </Stack>
          </Stack>

          {/* Right: floating pill cards + dashed connectors */}
          <Box
            sx={{
              flex: { md: "1 1 auto" },
              position: "relative",
              width: "100%",
              height: { xs: "auto", md: 500, lg: 540 },
              display: { xs: "flex", md: "block" },
              flexDirection: { xs: "column" },
              gap: { xs: 1.75 },
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                position: "absolute",
                inset: 0,
                display: { xs: "none", md: "block" },
                zIndex: 1,
                pointerEvents: "none",
              }}
            >
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                width="100%"
                height="100%"
              >
                {connectorSegments.map((seg, index) => {
                  const isLit = hoveredId === seg.id;
                  return (
                    <motion.line
                      key={seg.id}
                      x1={seg.from.x}
                      y1={seg.from.y}
                      x2={seg.to.x}
                      y2={seg.to.y}
                      stroke="#00D5C8"
                      strokeWidth={isLit ? 0.32 : 0.2}
                      strokeLinecap="round"
                      strokeDasharray="1.4 1.8"
                      opacity={isLit ? 0.6 : 0.28}
                      initial={{ pathLength: 0 }}
                      animate={isVisible ? { pathLength: 1 } : undefined}
                      transition={{
                        duration: 1,
                        delay: 0.4 + index * 0.1,
                        ease: "easeInOut",
                      }}
                    />
                  );
                })}
              </svg>
            </Box>

            {localizedServices.map((service, index) => (
              <ServicePill
                key={service.id}
                service={service}
                index={index}
                isVisible={isVisible}
                isMobile={isMobile}
                isActive={activeId === service.id}
                reducedMotion={reducedMotion}
                onActivate={() => setHoveredId(service.id)}
                onDeactivate={() => setHoveredId(null)}
              />
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ServicesSection;
